import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { activitiesApi, recommendationsApi } from '../lib/api';
import { getCategoryInfo, formatDate, formatPrice, getNextDate, formatDateHeroShort } from '../lib/utils';
import ActivityCard from './ActivityCard';
import ActivityCardSkeleton from './ActivityCardSkeleton';
import CategoryFilter from './CategoryFilter';
import SearchBar from './SearchBar';
import { ChevronLeft, ChevronRight, Heart, Star, Search } from 'lucide-react';
import analytics from '../lib/analytics';

// ====== HERO CAROUSEL ======
const HeroCarousel = ({ featured, language }) => {
  const [current, setCurrent] = useState(0);
  const intervalRef = useRef(null);
  const touchStart = useRef(null);

  const startAutoPlay = useCallback(() => {
    clearInterval(intervalRef.current);
    if (featured.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % featured.length);
    }, 5000);
  }, [featured.length]);

  useEffect(() => {
    startAutoPlay();
    return () => clearInterval(intervalRef.current);
  }, [startAutoPlay]);

  const goTo = (idx) => {
    setCurrent(idx);
    startAutoPlay();
  };

  const handleTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
    clearInterval(intervalRef.current);
  };

  const handleTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        goTo((current + 1) % featured.length);
      } else {
        goTo((current - 1 + featured.length) % featured.length);
      }
    } else {
      startAutoPlay();
    }
    touchStart.current = null;
  };

  if (featured.length === 0) return null;
  const act = featured[current];
  const price = formatPrice(act.price, language === 'es' ? 'es-MX' : 'en-US');
  const nextDate = getNextDate(act.dates);

  return (
    <div
      className="relative w-full h-[400px] md:h-[520px] overflow-hidden"
      data-testid="hero-carousel"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Background image — cleaner, no heavy dark overlay */}
      <div className="absolute inset-0 transition-opacity duration-700">
        <img 
          src={(act.cover_image || '').replace('w=800', 'w=1600').replace('w=400', 'w=1600').replace('w=600', 'w=1600') || 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=1600&q=80'} 
          alt={act.title}
          className="w-full h-full object-cover object-center"
        />
        {/* Bottom blur zone — taller for text readability */}
        <div className="absolute inset-x-0 bottom-0 h-[65%] bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-[180px] md:h-[200px] backdrop-blur-[8px]" style={{ WebkitMaskImage: 'linear-gradient(to top, black 50%, transparent)', maskImage: 'linear-gradient(to top, black 50%, transparent)' }} />
      </div>

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-8 text-white z-10">
        <div className="max-w-5xl mx-auto">
          <Link to={`/activity/${act.activity_id}`} className="block group">
            <p className="text-[11px] md:text-xs font-medium text-white/60 mb-0.5">
              {act.category && act.category.charAt(0).toUpperCase() + act.category.slice(1)}
              {act.city && ` · ${act.city}`}
            </p>
            <h2 className="text-lg md:text-2xl font-medium mb-1 group-hover:underline decoration-1 underline-offset-4 line-clamp-2">
              {act.title}
            </h2>
            <p className="text-xs md:text-sm text-white/70 line-clamp-1 max-w-xl mb-2">
              {act.short_description}
            </p>
            <div className="flex items-center gap-2 text-xs md:text-sm text-white/80">
              {price ? (
                <span className="font-medium">{price}</span>
              ) : (
                <span className="font-medium text-emerald-300">Gratis</span>
              )}
              {nextDate && (
                <>
                  <span className="text-white/40">·</span>
                  <span>{formatDateHeroShort(nextDate.date, language === 'es' ? 'es-MX' : 'en-US')}</span>
                </>
              )}
            </div>
          </Link>
        </div>
      </div>

      {/* Dots */}
      {featured.length > 1 && (
        <div className="absolute bottom-3 right-5 md:right-8 flex gap-1.5 z-10">
          {featured.map((_, idx) => (
            <button
              key={`hero-dot-${idx}`}
              onClick={() => goTo(idx)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                idx === current ? 'w-6 bg-white' : 'w-1.5 bg-white/40'
              }`}
              data-testid={`hero-dot-${idx}`}
            />
          ))}
        </div>
      )}

      {/* Navigation arrows (desktop only) */}
      {featured.length > 1 && (
        <>
          <button
            onClick={() => goTo((current - 1 + featured.length) % featured.length)}
            className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors z-10"
            data-testid="hero-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => goTo((current + 1) % featured.length)}
            className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors z-10"
            data-testid="hero-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}
    </div>
  );
};

// ====== HORIZONTAL SCROLL ROW ======
const RecommendationRow = ({ title, subtitle, activities, favoriteIds, onFavoriteToggle }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
  }, [activities]);

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.75;
    el.scrollBy({ left: direction === 'right' ? amount : -amount, behavior: 'smooth' });
  };

  if (!activities || activities.length === 0) return null;

  return (
    <div className="relative group/row mb-6" data-testid={`recommendation-row-${title.replace(/\s/g, '-').toLowerCase()}`}>
      {/* Title */}
      <div className="px-4 md:px-8 mb-2">
        <h3 className="text-base md:text-lg font-medium text-gray-800">{title}</h3>
        {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
      </div>

      {/* Scroll container */}
      <div className="relative">
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-3 md:gap-4 overflow-x-auto scrollbar-hide scroll-smooth pl-4 md:pl-8 pr-4"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {activities.map((activity) => (
            <div key={activity.activity_id} className="flex-shrink-0 w-[44vw] md:w-[200px]">
              <ActivityCard
                activity={activity}
                isFavorited={favoriteIds.includes(activity.activity_id)}
                onFavoriteToggle={onFavoriteToggle}
                compact
              />
            </div>
          ))}
          {/* Right spacer */}
          <div className="flex-shrink-0 w-4 md:w-8" aria-hidden="true" />
        </div>

        {/* Desktop scroll arrows */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="hidden md:flex absolute left-1 top-[80px] w-9 h-9 items-center justify-center bg-white rounded-full shadow-lg border border-gray-200 opacity-0 group-hover/row:opacity-100 transition-opacity hover:scale-105 z-10"
            data-testid="row-scroll-left"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="hidden md:flex absolute right-1 top-[80px] w-9 h-9 items-center justify-center bg-white rounded-full shadow-lg border border-gray-200 opacity-0 group-hover/row:opacity-100 transition-opacity hover:scale-105 z-10"
            data-testid="row-scroll-right"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// ====== SEARCH RESULTS GRID (when searching/filtering) ======
const SearchResultsGrid = ({ selectedCategory, searchFilters, favoriteIds, onFavoriteToggle }) => {
  const { t } = useLanguage();
  const [activities, setActivities] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();

  useEffect(() => {
    setActivities([]);
    setSuggestions([]);
    setPage(1);
    setHasMore(true);
    setLoading(true);
  }, [selectedCategory, searchFilters]);

  const fetchActivities = useCallback(async (pageNum) => {
    try {
      const params = { page: pageNum, limit: 12 };
      if (selectedCategory) params.category = selectedCategory;
      if (searchFilters?.search) params.search = searchFilters.search;
      if (searchFilters?.location) params.location = searchFilters.location;
      if (searchFilters?.dateFrom) params.date_from = searchFilters.dateFrom;
      if (searchFilters?.dateTo) params.date_to = searchFilters.dateTo;
      const response = await activitiesApi.getAll(params);
      const newActivities = response.data.activities;
      if (pageNum === 1) {
        setActivities(newActivities);
        setSuggestions(response.data.suggestions || []);
      } else {
        setActivities(prev => [...prev, ...newActivities]);
      }
      setHasMore(pageNum < response.data.pages);
    } catch (error) {
      console.error('fetchActivities:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [selectedCategory, searchFilters]);

  useEffect(() => {
    fetchActivities(page);
  }, [fetchActivities, page]);

  const lastActivityRef = useCallback(node => {
    if (loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setLoadingMore(true);
        setPage(prev => prev + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loadingMore, hasMore]);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 pt-10">
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => <ActivityCardSkeleton key={`skeleton-${i}`} />)}
        </div>
      ) : activities.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            <Search className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-gray-500 text-lg font-medium mb-2">{t('search.noResults')}</p>
          <p className="text-gray-400 text-sm mb-8">Intenta con otros términos, ubicaciones o fechas</p>
          
          {suggestions.length > 0 && (
            <div className="text-left max-w-5xl mx-auto">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 px-2">Otras experiencias que te pueden interesar</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {suggestions.map(activity => (
                  <ActivityCard
                    key={activity.activity_id}
                    activity={activity}
                    isFavorited={favoriteIds.includes(activity.activity_id)}
                    onFavoriteToggle={onFavoriteToggle}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-10">
          {activities.map((activity, index) => (
            <div
              key={activity.activity_id}
              ref={index === activities.length - 1 ? lastActivityRef : null}
              className="animate-fade-in"
              style={{ animationDelay: `${(index % 12) * 0.05}s` }}
            >
              <ActivityCard
                activity={activity}
                isFavorited={favoriteIds.includes(activity.activity_id)}
                onFavoriteToggle={onFavoriteToggle}
              />
            </div>
          ))}
        </div>
      )}
      {loadingMore && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-10">
          {[...Array(4)].map((_, i) => <ActivityCardSkeleton key={`loading-${i}`} />)}
        </div>
      )}
    </div>
  );
};

// ====== MAIN ACTIVITY FEED ======
const ActivityFeed = () => {
  const { t, language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [themes, setThemes] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loadingHome, setLoadingHome] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchFilters, setSearchFilters] = useState({});
  const isSearching = selectedCategory !== null || searchFilters.search || searchFilters.location || searchFilters.dateFrom;

  // Listen for logo click to clear search
  useEffect(() => {
    const handleClearSearch = () => {
      setSelectedCategory(null);
      setSearchFilters({});
    };
    window.addEventListener('utentik:clear-search', handleClearSearch);
    return () => window.removeEventListener('utentik:clear-search', handleClearSearch);
  }, []);

  // Fetch favorites
  useEffect(() => {
    if (isAuthenticated) {
      activitiesApi.getFavoriteIds().then(res => setFavoriteIds(res.data)).catch(() => {});
    }
  }, [isAuthenticated]);

  // Fetch recommendations + featured
  useEffect(() => {
    const fetchHome = async () => {
      setLoadingHome(true);
      try {
        const [recRes, featRes] = await Promise.all([
          recommendationsApi.getAll(),
          recommendationsApi.getFeatured()
        ]);
        setThemes(recRes.data.themes || []);
        setFeatured(featRes.data.featured || []);
      } catch (error) {
        console.error('fetchHome:', error);
      } finally {
        setLoadingHome(false);
      }
    };
    fetchHome();
  }, []);

  const handleSearch = (filters) => {
    setSearchFilters(filters);
    if (filters.search || filters.location || filters.dateFrom) {
      analytics.search(filters);
    }
  };
  const handleCategorySelect = (category) => setSelectedCategory(category);
  const handleFavoriteToggle = (id, fav) => {
    setFavoriteIds(prev => fav ? [...prev, id] : prev.filter(x => x !== id));
  };

  return (
    <div className="min-h-screen pb-32 md:pb-8" data-testid="activity-feed">
      {/* Search + Categories - fixed below header */}
      <div className="fixed top-14 md:top-11 left-0 right-0 z-30 bg-white shadow-sm pt-1.5 md:pt-2">
        <div className="pt-0 pb-1.5">
          <SearchBar onSearch={handleSearch} filters={searchFilters} />
        </div>
        <CategoryFilter
          selectedCategory={selectedCategory}
          onSelectCategory={handleCategorySelect}
        />
      </div>
      {/* Spacer for fixed search + categories — must match exactly */}
      <div className="h-[98px] md:h-[110px]" />

      {isSearching ? (
        /* === SEARCH/FILTER MODE === */
        <SearchResultsGrid
          selectedCategory={selectedCategory}
          searchFilters={searchFilters}
          favoriteIds={favoriteIds}
          onFavoriteToggle={handleFavoriteToggle}
        />
      ) : (
        /* === HOME / DISCOVERY MODE === */
        <div>
          {loadingHome ? (
            <div className="px-4 md:px-8 pt-4">
              {/* Hero skeleton */}
              <div className="w-full h-[300px] md:h-[400px] bg-gray-100 rounded-2xl animate-pulse mb-8" />
              {/* Row skeletons */}
              {[1, 2].map(i => (
                <div key={i} className="mb-8">
                  <div className="h-6 w-48 bg-gray-100 rounded animate-pulse mb-4" />
                  <div className="flex gap-4">
                    {[1, 2, 3, 4].map(j => (
                      <div key={j} className="flex-shrink-0 w-[260px]">
                        <ActivityCardSkeleton />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Hero Carousel */}
              {featured.length > 0 && (
                <div className="mb-8">
                  <HeroCarousel featured={featured} language={language} />
                </div>
              )}

              {/* Recommendation Rows */}
              {themes.length > 0 ? (
                <div className="pt-2">
                  {themes.map((theme) => (
                    <RecommendationRow
                      key={theme.theme_id}
                      title={theme.title}
                      subtitle={theme.subtitle}
                      activities={theme.activities}
                      favoriteIds={favoriteIds}
                      onFavoriteToggle={handleFavoriteToggle}
                    />
                  ))}
                </div>
              ) : (
                /* Fallback when no themed recommendations available */
                <div className="text-center py-16 px-4">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-50 flex items-center justify-center">
                    <Search className="w-10 h-10 text-gray-200" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-400 mb-2">
                    Próximamente más actividades
                  </h3>
                  <p className="text-sm text-gray-300 max-w-md mx-auto">
                    Estamos preparando experiencias increíbles para ti. Usa el buscador para encontrar actividades disponibles.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
